import React, { useState } from "react";
import {
  Box,
  Text,
  Stack,
  Group,
  Flex,
  List,
  Grid,
  Button,
  ThemeIcon,
  Tooltip,
  Badge,
} from "@mantine/core";
import { IconQuestionMark } from "@tabler/icons-react";
import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";
import { CustomTooltip } from "@components/shared";

export default function SnapshotQueryVisualizer({
  data,
  mini = false,
  children,
}) {
  if (!data || !data.display_components) return null;
  const { data: queryData, display_components: displayComponents } = data;
  // not set up yet
  if (!displayComponents || displayComponents.length === 0) return null;

  let formattedDisplayComponents = displayComponents
    .map((m) => ({
      ...m,
      value: queryData[m.query_data_request] || m.value,
    }))
    .filter(
      (m) =>
        m.value !== undefined &&
        m.value !== null &&
        JSON.stringify(m.value) !== "{}" &&
        m.value.length > 0
    );

  if (mini) {
    return (
      <Flex gap="xs" align="center">
        {formattedDisplayComponents.map((m, idx) => (
          <MiniQueryComponentVisualizer key={idx} component={m} />
        ))}
      </Flex>
    );
  }

  return (
    <Box mt="xs">
      <Stack gap="md">
        {formattedDisplayComponents.map((m, idx) => (
          <QueryComponentVisualizer key={idx} component={m} />
        ))}
        {/* {data.organizations?.length > 0 && (
          <>
            <Flex gap="xs" align="center">
              <span>{entityIcon.organization(14)}</span>
              <Text fw={600} size="sm">
                Organizations
              </Text>
            </Flex>
            <List size="sm">
              {data.organizations.map((m, idx) => (
                <List.Item key={idx}>{m.name}</List.Item>
              ))}
            </List>
          </>
        )}
        {data.locations?.length > 0 && (
          <>
            <Flex gap="xs" align="center">
              <span>{entityIcon.location(14)}</span>
              <Text fw={600} size="sm">
                Locations
              </Text>
            </Flex>
            <List size="sm">
              {data.locations.map((m, idx) => (
                <List.Item key={idx}>{m.name}</List.Item>
              ))}
            </List>
          </>
        )}
        {data.query_components?.length > 0 && (
          <>
            {data.query_components.map((m, idx) => (
              <QueryComponentVisualizer key={idx} component={m} />
            ))}
          </>
        )} */}
      </Stack>
    </Box>
  );
}

const MiniQueryComponentVisualizer = ({ component }) => {
  const entityConfig = getEntityConfig(component.server_name);
  const baseColor = entityConfig.color;

  const badgeConfig = {
    variant: "light",
    size: "md",
    color: baseColor,
  };

  if (["subject_organizations"].includes(component.server_name)) {
    return (
      <EntityTooltip items={component.value}>
        <Badge {...badgeConfig} leftSection={entityIcon.organization(12)}>
          {component.value.length}
        </Badge>
        {/* <Flex gap="xs" align="center">
          <ThemeIcon color={baseColor} size="sm" variant="light" radius="xl">
            {entityIcon.organization(12)}
          </ThemeIcon>
          <Text size="xs" fw={600}>
            {component.value.length}
          </Text>
        </Flex> */}
      </EntityTooltip>
    );
  }
  if (["campaign_efforts"].includes(component.server_name)) {
    return (
      <EntityTooltip items={component.value.map((m) => ({ name: m.title }))}>
        <Badge {...badgeConfig} leftSection={entityIcon.effort(12)}>
          {component.value.length}
        </Badge>
      </EntityTooltip>
    );
  }
  if (["subject_locations", "locations"].includes(component.server_name)) {
    return (
      <EntityTooltip items={component.value}>
        <Badge {...badgeConfig} leftSection={entityIcon.location(12)}>
          {component.value.length}
        </Badge>
      </EntityTooltip>
    );
  }
  if (["keywords"].includes(component.server_name)) {
    return (
      <EntityTooltip items={component.value}>
        <Badge {...badgeConfig} leftSection={entityIcon.keyword(12)}>
          {component.value.length}
        </Badge>
      </EntityTooltip>
    );
  }
  if (["contests"].includes(component.server_name)) {
    return (
      <EntityTooltip items={component.value}>
        <Badge {...badgeConfig} leftSection={entityIcon.contest(12)}>
          {component.value.length}
        </Badge>
      </EntityTooltip>
    );
  }

  if (["questions"].includes(component.server_name)) {
    return (
      <EntityTooltip items={component.value.map((m) => ({ name: m.title }))}>
        <Badge {...badgeConfig} leftSection={<IconQuestionMark size={12} />}>
          {component.value.length}
        </Badge>
      </EntityTooltip>
    );
  }

  return null;
};

const EntityTooltip = ({ items, children }) => {
  const maxLength = 10;
  const formattedItems = items
    .slice(0, maxLength)
    .map((m) => m.name)
    .join(", ");
  return (
    <Tooltip
      label={
        formattedItems +
        (items.length > maxLength
          ? ` and ${items.length - maxLength} more`
          : "")
      }
    >
      {children}
    </Tooltip>
  );
};

function QueryComponentVisualizer({ component }) {
  const valueIsArray = Array.isArray(component.value);
  const valueIsObject = typeof component.value === "object" && !valueIsArray;
  const valueIsString = typeof component.value === "string";

  return (
    <Flex gap="xs" direction="column">
      <div>
        <ComponentTitle component={component} />
      </div>
      {(valueIsString || valueIsObject) && (
        <Box
          style={{
            border: `1px solid var(--mantine-color-gray-2)`,
            borderRadius: "var(--mantine-radius-md)",
            padding: "var(--mantine-spacing-xs)",
            backgroundColor: "var(--mantine-color-gray-0)",
          }}
        >
          {valueIsString && <Text size="sm">{component.value}</Text>}
          {valueIsObject && <Text size="sm">{component.value.label}</Text>}
        </Box>
      )}
      {/* {valueIsArray && (
        <List size="sm">
          {component.value.map((m, idx) => (
            <List.Item key={idx}>
              <span style={{ fontWeight: 600 }}>{m.name || m.title}</span>
              {m.start_date && m.end_date && (
                <>
                  <br />
                  <span>
                    {new Date(m.start_date).toLocaleString()} -{" "}
                    {new Date(m.end_date).toLocaleString()}
                  </span>
                </>
              )}
              {m.city && m.state_province && (
                <>
                  <br />
                  <span>
                    {m.city}, {m.state_province}
                  </span>
                </>
              )}
            </List.Item>
          ))}
        </List>
      )} */}
      {valueIsArray && (
        <ArrayDisplayComponent
          values={component.value}
          entity={component.server_name}
        />
      )}
    </Flex>
  );
}

const ArrayDisplayComponent = ({ values = [], entity }) => {
  const itemsToShow = 6;
  const [open, setOpen] = useState(false);
  const formattedValues = values.slice(0, open ? values.length : itemsToShow);
  const entityConfig = getEntityConfig(entity);

  function getEntityColor() {
    return entityConfig.color;
  }

  const baseColor = getEntityColor();

  function getGridSpan() {
    // lay out into three columns
    if (values.length >= 8) {
      return 4;
    }
    // lay out into two columns
    if (values.length >= 2) {
      return 6;
    }
    // single column
    return 12;
  }

  const gridSpan = getGridSpan();

  return (
    <Grid>
      {formattedValues.map((m, idx) => (
        <Grid.Col span={{ base: 12, md: gridSpan }} gap="xs" key={idx}>
          <Box
            style={{
              border: `1px solid var(--mantine-color-${baseColor}-2)`,
              borderRadius: "var(--mantine-radius-md)",
              padding: "var(--mantine-spacing-xs)",
              backgroundColor: `var(--mantine-color-${baseColor}-0)`,
            }}
          >
            <Stack gap={1}>
              <Text size="sm" fw={600}>
                {m.name || m.title}
              </Text>
              {m.start_date && m.end_date && (
                <>
                  <Text size="xs">
                    {new Date(m.start_date).toLocaleDateString()} -{" "}
                    {new Date(m.end_date).toLocaleDateString()}
                  </Text>
                </>
              )}
              {m.city && m.state_province && (
                <>
                  <Text size="xs">
                    {m.city}, {m.state_province}
                  </Text>
                </>
              )}
              {entity === "questions" && m.selected_items?.length > 0 && (
                <Text size="xs">
                  {m.selected_items.map((m) => m.label).join(", ")}
                </Text>
              )}
            </Stack>
          </Box>
        </Grid.Col>
      ))}
      {values.length > 3 && (
        <Grid.Col span={12}>
          <Button
            size="xs"
            variant="light"
            color={baseColor}
            onClick={() => setOpen(!open)}
          >
            {open ? `Show Less` : `Show More (${values.length - itemsToShow})`}
          </Button>
        </Grid.Col>
      )}
    </Grid>
  );
};

const ComponentTitle = ({ component }) => {
  return (
    <>
      {component.tooltip_text ? (
        <CustomTooltip label={component.tooltip_text}>
          <Flex gap="xs" align="center">
            <EntityIconDisplay entity={component.server_name} />
            <Text fw={600} size="sm">
              {component.label}
            </Text>
          </Flex>
        </CustomTooltip>
      ) : (
        <Flex gap="xs" align="center">
          <EntityIconDisplay entity={component.server_name} />
          <Text fw={600} size="sm">
            {component.label}
          </Text>
        </Flex>
      )}
    </>
  );
};

const EntityIconDisplay = ({ entity }) => {
  const entityColor = getEntityConfig(entity)?.color || "gray";
  if (["subject_organizations"].includes(entity)) {
    return (
      <ThemeIcon color={entityColor} size="sm" variant="light" radius="xl">
        {entityIcon.organization(12)}
      </ThemeIcon>
    );
  }

  if (["campaign_efforts"].includes(entity)) {
    return (
      <ThemeIcon color={entityColor} size="sm" variant="light" radius="xl">
        {entityIcon.effort(12)}
      </ThemeIcon>
    );
  }

  if (["subject_locations", "locations"].includes(entity)) {
    return (
      <ThemeIcon color={entityColor} size="sm" variant="light" radius="xl">
        {entityIcon.location(12)}
      </ThemeIcon>
    );
  }

  if (["keywords"].includes(entity)) {
    return (
      <ThemeIcon color={entityColor} size="sm" variant="light" radius="xl">
        {entityIcon.keyword(12)}
      </ThemeIcon>
    );
  }

  if (["contests"].includes(entity)) {
    return (
      <ThemeIcon color={entityColor} size="sm" variant="light" radius="xl">
        {entityIcon.contest(12)}
      </ThemeIcon>
    );
  }

  if (entity.toLowerCase().includes("date")) {
    return (
      <ThemeIcon color={entityColor} size="sm" variant="light" radius="xl">
        {entityIcon.calendar(12)}
      </ThemeIcon>
    );
  }

  if (entity.toLowerCase().includes("question")) {
    return (
      <ThemeIcon color="gray" size="sm" variant="light" radius="xl">
        <IconQuestionMark size={12} />
      </ThemeIcon>
    );
  }

  return null;
};

function getEntityConfig(entity) {
  if (["subject_organizations"].includes(entity)) {
    return {
      color: entityColor.organization,
    };
  }
  if (["campaign_efforts"].includes(entity)) {
    return {
      color: entityColor.effort,
    };
  }
  if (["subject_locations", "locations"].includes(entity)) {
    return {
      color: entityColor.location,
    };
  }
  if (["keywords"].includes(entity)) {
    return {
      color: entityColor.keyword,
    };
  }
  if (["contests"].includes(entity)) {
    return {
      color: entityColor.contest,
    };
  }
  return {
    color: "gray",
    icon: null,
  };
}
